import { FacilityAsset, CriterionKey } from '../../models/types';

/* One system's assets as a downloadable CSV — the assessment editor's "Export CSV" and both report
   tiers (summary and detailed) all call this, so the three stay byte-for-byte identical instead of
   three hand-maintained copies drifting apart. Each criterion's note gets its own column, mirroring
   the workbook's note-per-criterion rows. `opts` is for filtered exports from the editor: `system`
   adds a leading System column (the Summary tab's list spans all four), `suffix` tags the filename. */
export function exportSystemCsv(
  buildingName: string, systemKey: string, assets: FacilityAsset[],
  opts: { system?: (a: FacilityAsset) => string; suffix?: string } = {},
): void {
  const note = (a: FacilityAsset, k: CriterionKey) =>
    a.criterionNotes?.[k]
      ?? (k === 'visualInspection' ? a.visualIssues : k === 'operationalTest' ? a.operationalIssues : undefined)
      ?? '';
  const rows = assets.map(a => [
    ...(opts.system ? [opts.system(a)] : []),
    a.assetId, a.description, a.location, a.manufacturer, a.model, a.serialNumber,
    a.age, note(a, 'age'), a.remainingServiceLife, note(a, 'remainingServiceLife'),
    a.visualInspection, note(a, 'visualInspection'), a.operationalTest, note(a, 'operationalTest'),
    a.maintenanceRecords, note(a, 'maintenanceRecords'),
    a.maintenanceServiceLevel, note(a, 'maintenanceServiceLevel'),
    a.score ?? '', a.rating ?? '', a.notes,
  ]);
  const header = [...(opts.system ? ['System'] : []), 'Asset ID', 'Description', 'Location', 'Manufacturer', 'Model', 'Serial #',
    'Age (yrs)', 'Age Notes', 'RSL (yrs)', 'RSL Notes', 'Visual', 'Visual Notes',
    'Operational', 'Operational Notes', 'Maint. Records', 'Maint. Records Notes',
    'Service Level', 'Service Level Notes', 'Score', 'Rating', 'General Notes'];
  const csv = [header, ...rows]
    // null/undefined (unset RSL, visual, operational, score, rating) export as an empty cell.
    .map(r => r.map(v => `"${(v == null ? '' : String(v)).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = `${buildingName || 'facility'}-${systemKey}-assets${opts.suffix ? `-${opts.suffix}` : ''}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}
